import React, { useCallback, useState } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useFormik } from 'formik'
import { useDispatch } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { RefreshActions } from '../state/refresh'
import { isStringNullOrEmpty, validateMail } from '../utils/validators'
import { ErrorAlert } from '../formik/ErrorAlert'
import { Role, User } from '../types/user'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { InputLabel, Select, MenuItem, FormControl } from '@mui/material'

interface NewOrEditUserDialogProps {
  user: User | null
  handleClose: (b: boolean) => void
}

const NewOrEditUserDialog = ({
  user,
  handleClose
}: NewOrEditUserDialogProps): JSX.Element => {
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordMatchError, setPasswordMatchError] = useState<string | null>(
    null
  )
  const [changePassword, setChangePassword] = useState(false)

  const initUser = {
    id: user?.id,
    username: user?.username,
    firstName: user?.firstName,
    lastName: user?.lastName,
    email: user?.email,
    role: user?.role,
    password: undefined,
    passwordAgain: undefined
  }

  const validate = (
    user: Partial<User> & { passwordAgain: string | undefined }
  ): Partial<User> => {
    const errors: any = {}

    setPasswordError(null)
    setPasswordMatchError(null)

    if (!user?.username || user.username === '') {
      errors.username = 'Username cannot be empty.'
    }

    if (!user?.firstName || user.firstName === '') {
      errors.firstName = 'First name cannot be empty.'
    }

    if (!user?.lastName || user.lastName === '') {
      errors.lastName = 'Last name cannot be empty.'
    }

    if (!user?.email || user.email === '') {
      errors.email = 'Email cannot be empty.'
    }

    if (!user?.role) {
      errors.role = 'Choose role.'
    }

    user.email !== undefined &&
      validateMail(user.email ?? null, e => (errors.email = e))

    if (
      (user.id === undefined || changePassword) &&
      (user.password === undefined ||
        user.password === null ||
        user.password === '')
    ) {
      errors.password = 'Passwords should not be empty'
    }

    if (
      (user.id === undefined || changePassword) &&
      (user.passwordAgain === undefined ||
        user.passwordAgain === null ||
        user.passwordAgain === '')
    ) {
      errors.password = 'Passwords should not be empty'
    }

    if (user.password !== user.passwordAgain) {
      errors.passwordAgain = 'Passwords should match'
    }

    return errors
  }

  const onSubmit = useCallback(
    async (
      user: Partial<User> & { passwordAgain: string | undefined }
    ): Promise<void> => {
      handleClose(true)
      try {
        const isEdited = user.id !== undefined

        delete user.passwordAgain

        if (isEdited) {
          if (!changePassword) {
            delete user.password
            delete user.passwordAgain
          }
          await requestMaker.updateUser(user)
        } else {
          await requestMaker.createUser(user)
        }
        dispatch(RefreshActions.refreshUserState())
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Succesfully update user'
          })
        )
        handleClose(true)
      } catch (e) {
        dispatch(
          RefreshActions.setToastMessage({
            message:
              'Error - fill all neccessary fields correctly and try again'
          })
        )
      }
    },
    [requestMaker, dispatch]
  )

  const formik = useFormik({
    initialValues: initUser,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  return (
    <div>
      <Dialog open onClose={handleClose}>
        <form>
          <DialogTitle>User</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              error={!!formik.errors.username && formik.touched.username}
              helperText={
                !!formik.errors.username && formik.touched.username
                  ? formik.errors.username
                  : null
              }
              margin='dense'
              id='username'
              label='Username'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.username}
              onChange={formik.handleChange}
              required
            />
            <TextField
              autoFocus
              error={!!formik.errors.firstName && formik.touched.firstName}
              helperText={
                !!formik.errors.firstName && formik.touched.firstName
                  ? formik.errors.firstName
                  : null
              }
              margin='dense'
              id='firstName'
              label='First Name'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.firstName}
              onChange={formik.handleChange}
              required
            />
            <TextField
              autoFocus
              error={!!formik.errors.lastName && formik.touched.lastName}
              helperText={
                !!formik.errors.lastName && formik.touched.lastName
                  ? formik.errors.lastName
                  : null
              }
              margin='dense'
              id='lastName'
              label='Last Name'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.lastName}
              onChange={formik.handleChange}
              required
            />
            <TextField
              margin='dense'
              error={!!formik.errors.email && formik.touched.email}
              helperText={
                !!formik.errors.email && formik.touched.email
                  ? formik.errors.email
                  : null
              }
              id='email'
              label='Email'
              type='email'
              fullWidth
              variant='standard'
              value={formik.values.email}
              onChange={formik.handleChange}
              required
            />
            <FormControl fullWidth style={{ marginTop: '1vw' }}>
              <InputLabel id='role'>Role</InputLabel>
              <Select
                labelId='role'
                error={!!formik.errors.role && formik.touched.role}
                required
                id='role'
                value={formik.values.role}
                label='Role'
                onChange={e =>
                  void formik.setFieldValue('role', e.target.value)}
              >
                {Object.keys(Role)
                  ?.filter(r => r !== Role.SUPERADMIN)
                  .map((r, index) => (
                    <MenuItem key={index} value={r}>
                      {r}
                    </MenuItem>
                  ))}
              </Select>
              <ErrorAlert formik={formik} name='role' />
            </FormControl>
            {formik.values.id !== undefined && (
              <FormControlLabel
                control={
                  <Checkbox
                    value='changePasswordCheckbox'
                    color='primary'
                    onClick={() => setChangePassword(prev => !prev)}
                  />
                }
                label='Change password'
              />
            )}
            {(formik.values.id === undefined || changePassword) && (
              <>
                <TextField
                  error={
                    formik.touched.password &&
                    formik.touched.passwordAgain &&
                    !!formik.errors.password
                  }
                  helperText={
                    formik.touched.password &&
                    formik.touched.passwordAgain &&
                    !!formik.errors.password
                      ? formik.errors.password
                      : null
                  }
                  margin='dense'
                  id='password'
                  label='Password'
                  type='password'
                  fullWidth
                  variant='standard'
                  required
                  value={formik.values.password}
                  onChange={formik.handleChange}
                />
                <TextField
                  error={
                    formik.touched.password &&
                    formik.touched.passwordAgain &&
                    !!formik.errors.passwordAgain
                  }
                  helperText={
                    formik.touched.password &&
                    formik.touched.passwordAgain &&
                    !!formik.errors.passwordAgain
                      ? formik.errors.passwordAgain
                      : null
                  }
                  margin='dense'
                  id='passwordAgain'
                  label='Password again'
                  type='password'
                  fullWidth
                  required
                  variant='standard'
                  value={formik.values.passwordAgain}
                  onChange={formik.handleChange}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose as any}>Cancel</Button>
            <Button onClick={formik.handleSubmit as any}>Add</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}

export default NewOrEditUserDialog
